import { For, Show, createSignal, type Component } from 'solid-js';

import { SETTINGS, SLIDERS, type Settings } from '../state/settings';
import type { GameStore } from '../state/store';

/**
 * The escape menu: settings, and the way out.
 *
 * Opened by Escape from a clear screen — Escape is a back button first, so it
 * closes any other panel before it opens this one. Abandoning a run is behind a
 * confirmation, because a seasonal character is hours of work and "Escape,
 * Enter" is a very short accident.
 */
export const SystemMenu: Component<{ store: GameStore }> = (props) => {
  const [confirming, setConfirming] = createSignal(false);

  const close = () => {
    setConfirming(false);
    props.store.closeMenu();
  };

  return (
    <Show when={props.store.menu() === 'system'}>
      <div
        class="system hud-interactive"
        data-testid="system-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
      >
        <div class="plate plate--riveted system__panel">
          <h2 class="system__title">Respite</h2>

          <section class="system__settings" aria-label="Settings">
            <h3 class="system__heading">Settings</h3>

            <For each={SETTINGS}>
              {(setting) => {
                const on = () => props.store.settings()[setting.key];
                const available = () => props.store.settingAvailable(setting.key);
                return (
                  <label
                    class="toggle"
                    classList={{ 'toggle--unavailable': !available() }}
                    data-testid={`setting-${setting.key}`}
                  >
                    <input
                      type="checkbox"
                      class="toggle__input"
                      checked={on()}
                      disabled={!available()}
                      data-testid={`toggle-${setting.key}`}
                      onChange={(e) =>
                        props.store.setSetting(setting.key, e.currentTarget.checked)
                      }
                    />
                    <span class="toggle__box" aria-hidden="true">
                      {on() ? '✓' : ''}
                    </span>
                    <span class="toggle__text">
                      <span class="toggle__label">{setting.label}</span>
                      <span class="toggle__hint">
                        <Show
                          when={available()}
                          fallback="Unavailable in this session — the effect could not be built."
                        >
                          {setting.hint}
                        </Show>
                      </span>
                    </span>
                  </label>
                );
              }}
            </For>

            {/*
              The settings that are not switches. Sliders rather than mute
              toggles because "quieter" is what a player actually wants; zero is a
              real position on one, so there is no second control for silence.
            */}
            <For each={SLIDERS}>
              {(slider) => {
                const percent = () => Math.round(props.store.settings()[slider.key] * 100);
                return (
                  <label class="slider" data-testid={`setting-${slider.key}`}>
                    <span class="slider__text">
                      <span class="slider__label">{slider.label}</span>
                      <span class="slider__hint">{slider.hint}</span>
                    </span>
                    <input
                      type="range"
                      class="slider__input"
                      min="0"
                      max="100"
                      step="1"
                      value={percent()}
                      data-testid={`${slider.key}-input`}
                      aria-label={slider.label}
                      onInput={(e) =>
                        props.store.setVolume(slider.key, e.currentTarget.valueAsNumber / 100)
                      }
                    />
                    <span class="slider__value" data-testid={`${slider.key}-value`}>
                      {percent()}
                    </span>
                  </label>
                );
              }}
            </For>

            {/*
              Everything here takes effect at once. A setting that quietly needs
              a reload is worse than one that does not exist, which is why the
              `specular` switch stays a URL flag rather than appearing on this
              list — see state/settings.ts.
            */}
            <p class="system__note">Changes apply immediately and are remembered.</p>
          </section>

          <div class="system__actions">
            <button
              type="button"
              class="sigil-button system__resume"
              data-testid="system-resume"
              onClick={close}
            >
              <span>Resume</span>
            </button>

            <Show
              when={confirming()}
              fallback={
                <button
                  type="button"
                  class="system__leave"
                  data-testid="system-return"
                  onClick={() => setConfirming(true)}
                >
                  Return to Title
                </button>
              }
            >
              <div class="system__confirm" data-testid="system-confirm">
                <p class="system__warning">
                  Abandon this run? The world does not keep your place.
                </p>
                <div class="system__confirm-actions">
                  <button
                    type="button"
                    class="system__leave system__leave--danger"
                    data-testid="system-return-confirm"
                    onClick={() => {
                      setConfirming(false);
                      props.store.returnToTitle();
                    }}
                  >
                    Abandon
                  </button>
                  <button
                    type="button"
                    class="system__leave"
                    data-testid="system-return-cancel"
                    onClick={() => setConfirming(false)}
                  >
                    Stay
                  </button>
                </div>
              </div>
            </Show>
          </div>
        </div>
      </div>
    </Show>
  );
};

/** Re-exported so specs can enumerate the settings without a second list. */
export type { Settings };
